/**
 * The route table.
 *
 * ── There is no gate in front of this, and now nothing behind one either ────
 * website-cloud's App.tsx opens with six auth states, because nothing there
 * renders until it knows who you are. This one renders the board for everybody
 * and asks nothing — of anybody, on any route.
 *
 * There used to be five `/developer*` routes that did their own check. They are
 * `internal.mauvely.com` now. What remains is entirely anonymous: no route
 * here issues an authenticated request, and there is nothing in the markup, the
 * network tab or the navigation to suggest this site has ever heard of an
 * account.
 *
 * `/developer*` still resolves, to a redirect, for exactly as long as somebody
 * might still have it bookmarked. It sends the browser off-origin and renders
 * nothing.
 */

import { useEffect } from 'react';
import { matchRoute, useRouter } from './router';
import { Status } from './pages/Status';
import { Incidents, IncidentDetail } from './pages/Incidents';
import { ConfirmSubscription, Unsubscribe } from './pages/Subscription';
import { Page, Panel } from './components/Chrome';
import { Link } from './router';
import { display, monoPlain } from './theme';
import { useT } from './i18n';

/** Where the developer area went. Derived from the origin in development so a
 *  local bookmark lands on the local console rather than production. */
function internalOrigin(): string {
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:8790`;
  return 'https://internal.mauvely.com';
}

export function App() {
  const { path } = useRouter();
  const p = path.length > 1 ? path.replace(/\/+$/, '') : path;

  if (p === '/') return <Status />;

  // Literal routes before the pattern that would swallow them.
  if (p === '/incidents') return <Incidents />;
  const incident = matchRoute('/incidents/:id', p);
  if (incident?.id) return <IncidentDetail id={incident.id} />;

  if (p === '/subscribe/confirm') return <ConfirmSubscription />;
  if (p === '/unsubscribe') return <Unsubscribe />;

  if (p === '/developer' || p.startsWith('/developer/')) return <MovedToInternal />;

  return <NotFound />;
}

/**
 * The developer area's forwarding address.
 *
 * A redirect rather than a 404 because the people who have these URLs typed
 * them from memory for months, and "that page is not here" would read as the
 * site being broken rather than as the page having moved. Deliberately does not
 * try to map the old sub-paths onto the new ones: the console's own navigation
 * is one click away and cannot go stale the way a mapping table would.
 */
function MovedToInternal() {
  const t = useT();
  const target = internalOrigin();

  useEffect(() => {
    window.location.replace(target);
  }, [target]);

  return (
    <Page>
      <Panel style={{ display: 'grid', gap: 10 }}>
        <h1 style={display(26)}>{t('app.moved.title')}</h1>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>{t('app.moved.body')}</p>
        <span>
          <a href={target} style={{ ...monoPlain, textDecoration: 'none' }}>
            {target.replace(/^https?:\/\//, '')} →
          </a>
        </span>
      </Panel>
    </Page>
  );
}

function NotFound() {
  const t = useT();
  return (
    <Page>
      <Panel style={{ display: 'grid', gap: 10 }}>
        <h1 style={display(26)}>{t('app.notfound.title')}</h1>
        <span>
          <Link to="/" style={{ ...monoPlain, textDecoration: 'none' }}>
            {t('app.notfound.back')}
          </Link>
        </span>
      </Panel>
    </Page>
  );
}
